// src/pages/Team/ViewExecutives.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import employeeService from '../../services/employee.service';
import Toast from '../../components/Toast/Toast';
import './TeamPages.css';

const ViewExecutives = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchExecutives();
  }, [currentPage, searchTerm]);

  const fetchExecutives = async () => {
    setLoading(true);
    try {
      const companyId = isSuperAdmin 
        ? localStorage.getItem('selectedCompanyId')
        : user?.companyId;

      if (!companyId) {
        setToast({ message: 'Please select a company first', type: 'error' });
        setLoading(false);
        return;
      }

      const response = await employeeService.getEmployees(
        companyId,
        currentPage,
        10,
        searchTerm,
        'EXECUTIVE'
      );

      if (response.success && response.data) {
        // response.data is the array of users directly
        const users = Array.isArray(response.data) ? response.data : [];
        setEmployees(users);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching executives:', error);
      setToast({ message: 'Failed to fetch executives', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="team-page-container">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="team-header">
        <h4 className="team-title">Executive Team</h4>
      </div>

      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search team members... 🔍"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading">Loading team members...</div>
        ) : (
          <table className="team-table">
            <thead>
              <tr>
                <th>NAME</th>
                <th>EMPLOYEE CODE</th>
                <th>ROLE</th>
                <th>COMPANY</th>
                <th>EMAIL</th>
                <th>MOBILE</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {employees.length > 0 ? (
                employees.map((employee) => (
                  <tr key={employee.id}>
                    <td>{employee.fullName || `${employee.firstName} ${employee.lastName}`}</td>
                    <td>{employee.employeeCode}</td>
                    <td>{employee.role?.shortCode || 'EX'}</td>
                    <td>{employee.company?.name || 'N/A'}</td>
                    <td>{employee.email}</td>
                    <td>{employee.phone}</td>
                    <td>
                      <span className={`status-badge ${employee.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                        {employee.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="no-data">
                    {searchTerm ? 'No executives found matching your search' : 'No executives available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            « Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next »
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewExecutives;